import { useState, KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Save, X, Hash, Tag, CheckCircle2, Zap, Shuffle, Lock, Ban, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { configureTriggerNode } from '@/services/api';
import type { FlowNode, TriggerItem } from '@/types/flow';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface TriggerConfigProps {
    node: FlowNode;
    onClose: () => void;
}

export default function TriggerConfig({ node, onClose }: TriggerConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [triggers, setTriggers] = useState<TriggerItem[]>(node.data.config?.triggers || []);
    const [isRandom, setIsRandom] = useState(node.data.is_random ?? false);
    const [blockRestartWhileRunning, setBlockRestartWhileRunning] = useState(
        node.data.block_restart_while_running ?? false
    );
    const [negativeKeywords, setNegativeKeywords] = useState<Array<{ word: string; match_type: 'contains' | 'exact' }>>(
        node.data.negative_keywords ?? []
    );
    const [newNegWord, setNewNegWord] = useState('');
    const [newNegMatch, setNewNegMatch] = useState<'contains' | 'exact'>('contains');
    const [newTrigger, setNewTrigger] = useState<TriggerItem>({
        trigger_type: 'keyword',
        trigger_value: '',
    });

    const hasWildcard = triggers.some(t => t.trigger_value === '#');

    const saveMutation = useMutation({
        mutationFn: () => configureTriggerNode(node.data.id, {
            node_name: nodeName,
            triggers,
            is_random: isRandom,
            block_restart_while_running: blockRestartWhileRunning,
            negative_keywords: negativeKeywords,
        }),
        onSuccess: () => {
            toast({ title: 'Gatilho salvo!', description: 'Configurações salvas com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleAddTrigger = () => {
        if (!newTrigger.trigger_value.trim()) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Digite um valor.' });
            return;
        }
        setTriggers([...triggers, { ...newTrigger }]);
        setNewTrigger({ ...newTrigger, trigger_value: '' });
    };

    const handleAddWildcard = () => {
        if (hasWildcard) return;
        setTriggers([...triggers, { trigger_type: 'keyword', trigger_value: '#' }]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTrigger();
        }
    };

    const handleRemoveTrigger = (index: number) => {
        setTriggers(triggers.filter((_, i) => i !== index));
    };

    const handleAddNegativeKeyword = () => {
        const word = newNegWord.trim();
        if (!word) return;
        // Dedup case-insensitive
        const exists = negativeKeywords.some(
            (k) => k.word.toLowerCase() === word.toLowerCase() && k.match_type === newNegMatch,
        );
        if (exists) {
            setNewNegWord('');
            return;
        }
        setNegativeKeywords([...negativeKeywords, { word, match_type: newNegMatch }]);
        setNewNegWord('');
    };

    const handleRemoveNegativeKeyword = (index: number) => {
        setNegativeKeywords(negativeKeywords.filter((_, i) => i !== index));
    };

    const handleNegKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddNegativeKeyword();
        }
    };

    const getTriggerIcon = (type: string) => {
        switch (type) {
            case 'keyword':
                return <Hash className="w-3.5 h-3.5" />;
            case 'tag':
                return <Tag className="w-3.5 h-3.5" />;
            case 'exact_match':
                return <CheckCircle2 className="w-3.5 h-3.5" />;
            default:
                return <Hash className="w-3.5 h-3.5" />;
        }
    };

    const getTriggerTypeLabel = (type: string) => {
        switch (type) {
            case 'keyword':
                return 'Palavra-chave';
            case 'tag':
                return 'Tag';
            case 'exact_match':
                return 'Correspondência Exata';
            default:
                return type;
        }
    };

    const getTriggerColor = (type: string) => {
        switch (type) {
            case 'keyword':
                return 'bg-cyan-50 border-cyan-200 text-cyan-700';
            case 'tag':
                return 'bg-blue-50 border-blue-200 text-blue-700';
            case 'exact_match':
                return 'bg-green-50 border-green-200 text-green-700';
            default:
                return 'bg-gray-50 border-gray-200 text-gray-700';
        }
    };

    return (
        <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Nome do Gatilho</Label>
                    <Input
                        value={nodeName}
                        onChange={(e) => setNodeName(e.target.value)}
                        placeholder="Ex: Boas-vindas"
                        className="h-10"
                    />
                </div>

                {/* Random */}
                <button
                    type="button"
                    onClick={() => setIsRandom(!isRandom)}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${isRandom
                            ? 'border-orange-300 bg-orange-50 text-orange-600'
                            : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50 text-gray-500 cursor-pointer'
                        }`}
                >
                    <Shuffle className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Próximo node aleatório</p>
                        <p className="text-xs opacity-70">Sorteia um dos nodes conectados a este gatilho</p>
                    </div>
                    {isRandom && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0 text-orange-500" />}
                </button>

                {/* Block restart while running */}
                <button
                    type="button"
                    onClick={() => setBlockRestartWhileRunning(!blockRestartWhileRunning)}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${blockRestartWhileRunning
                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                            : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50 text-gray-500 cursor-pointer'
                        }`}
                >
                    <Lock className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Não reiniciar enquanto o flow estiver em execução</p>
                        <p className="text-xs opacity-70">
                            Ignora gatilhos (palavra-chave ou #) se o lead já está em algum step.
                            Volta a funcionar quando o flow terminar.
                        </p>
                    </div>
                    {blockRestartWhileRunning && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0 text-amber-500" />}
                </button>

                {/* Wildcard rápido */}
                <button
                    type="button"
                    onClick={handleAddWildcard}
                    disabled={hasWildcard}
                    className={`w-full flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-all text-left
                        ${hasWildcard
                            ? 'border-purple-200 bg-purple-50 text-purple-400 cursor-not-allowed'
                            : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50 text-purple-600 cursor-pointer'
                        }`}
                >
                    <Zap className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium">Ativar com qualquer mensagem (#)</p>
                        <p className="text-xs opacity-70">O fluxo inicia para qualquer texto enviado pelo lead</p>
                    </div>
                    {hasWildcard && <CheckCircle2 className="w-4 h-4 ml-auto flex-shrink-0" />}
                </button>

                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Adicionar Gatilho por Palavra</Label>

                    <Select
                        value={newTrigger.trigger_type}
                        onValueChange={(value: TriggerItem['trigger_type']) =>
                            setNewTrigger({ ...newTrigger, trigger_type: value })
                        }
                    >
                        <SelectTrigger className="h-10">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="keyword">
                                <div className="flex items-center gap-2">
                                    <Hash className="w-4 h-4" />
                                    <span>Palavra-chave</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="tag">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4" />
                                    <span>Tag (ID)</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="exact_match">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Correspondência Exata</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <div className="relative">
                        <Input
                            value={newTrigger.trigger_value}
                            onChange={(e) => setNewTrigger({ ...newTrigger, trigger_value: e.target.value })}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                newTrigger.trigger_type === 'tag'
                                    ? 'Digite o ID da tag...'
                                    : 'Digite e pressione Enter...'
                            }
                            className="h-10"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            Enter ↵
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">
                        Gatilhos Configurados
                        {triggers.length > 0 && (
                            <span className="ml-2 text-xs font-normal text-gray-500">({triggers.length})</span>
                        )}
                    </Label>

                    {triggers.length === 0 ? (
                        <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Hash className="w-5 h-5 text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-500">Nenhum gatilho configurado</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Use o botão acima ou digite uma palavra-chave
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {triggers.map((trigger, index) => (
                                <div
                                    key={index}
                                    className={`
                                        group inline-flex items-center gap-2 border rounded-lg px-3 py-2
                                        transition-all hover:shadow-sm
                                        ${trigger.trigger_value === '#' ? 'bg-purple-50 border-purple-200 text-purple-700' : getTriggerColor(trigger.trigger_type)}
                                    `}
                                >
                                    <div className="flex items-center gap-1.5">
                                        {trigger.trigger_value === '#' ? <Zap className="w-3.5 h-3.5" /> : getTriggerIcon(trigger.trigger_type)}
                                        <div>
                                            <div className="text-xs opacity-70 leading-none mb-0.5">
                                                {getTriggerTypeLabel(trigger.trigger_type)}
                                            </div>
                                            <div className="text-sm font-medium leading-none">
                                                {trigger.trigger_value === '#' ? 'Qualquer mensagem' : trigger.trigger_value}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveTrigger(index)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 hover:bg-black/5 rounded p-0.5"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {triggers.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-900">
                            <strong>Dica:</strong> Gatilhos específicos têm prioridade sobre "qualquer mensagem".
                        </p>
                    </div>
                )}

                {/* ── Palavras negativas (bloqueiam '#') ────────────────── */}
                <div className="space-y-3 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                        <Ban className="w-4 h-4 text-rose-500 flex-shrink-0" />
                        <Label className="text-sm font-medium text-gray-700 m-0">
                            Palavras que NÃO ativam (apenas para #)
                        </Label>
                    </div>

                    {!hasWildcard && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-900">
                                Estas palavras só bloqueiam quando o gatilho <strong>#</strong> está ativo.
                                Para gatilhos específicos, são ignoradas.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-[1fr_auto] gap-2">
                        <Input
                            value={newNegWord}
                            onChange={(e) => setNewNegWord(e.target.value)}
                            onKeyDown={handleNegKeyDown}
                            placeholder="Ex: cancelar, preço..."
                            className="h-10"
                        />
                        <Select
                            value={newNegMatch}
                            onValueChange={(value: 'contains' | 'exact') => setNewNegMatch(value)}
                        >
                            <SelectTrigger className="h-10 w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="contains">Contém</SelectItem>
                                <SelectItem value="exact">Exata</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {negativeKeywords.length === 0 ? (
                        <p className="text-xs text-gray-400">Nenhuma palavra negativa configurada.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {negativeKeywords.map((kw, index) => (
                                <div
                                    key={index}
                                    className="group inline-flex items-center gap-2 border rounded-lg px-3 py-2
                                               bg-rose-50 border-rose-200 text-rose-700 transition-all hover:shadow-sm"
                                >
                                    <Ban className="w-3.5 h-3.5" />
                                    <div>
                                        <div className="text-xs opacity-70 leading-none mb-0.5">
                                            {kw.match_type === 'exact' ? 'Palavra exata' : 'Contém'}
                                        </div>
                                        <div className="text-sm font-medium leading-none">{kw.word}</div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveNegativeKeyword(index)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 hover:bg-black/5 rounded p-0.5"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="border-t bg-white p-6 flex-shrink-0">
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                    className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-medium"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
            </div>
        </>
    );
}
